import { Link } from "react-router-dom";
import { Phone, ArrowRight, Check } from "lucide-react";
import { COMPANY } from "../data/site";
import ContactCTA from "./ContactCTA";
import FAQ from "./FAQ";
import ServiceGallery from "./ServiceGallery";

/**
 * ServicePageTemplate — used by every individual service page so they share
 * the same structure: hero, intro, trust points, image strip, mini-process, CTA.
 */
export default function ServicePageTemplate({
  eyebrow = "Tjänst",
  title,
  intro,
  heroImage,
  heroImagePosition,
  bullets = [],
  bodyHeading,
  bodyText,
  steps,
  extraSection = null,
  faqItems = null,
  faqTitle = "Vanliga frågor",
  faqId = "service-faq",
}) {
  const defaultSteps = [
    { title: "Bedömning", text: "Vi tittar på platsen och förutsättningarna." },
    { title: "Tydlig offert", text: "Du får veta vad som ingår och vad det kostar." },
    { title: "Säkert utförande", text: "Vi utför arbetet enligt plan med rätt utrustning." },
  ];
  const stepList = steps || defaultSteps;

  return (
    <div data-testid="service-page" className="bg-[#F7F5F0]">
      {/* Hero */}
      <section
        data-testid="service-hero"
        className="relative min-h-[400px] md:min-h-[480px] flex items-end overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt={title}
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            style={heroImagePosition ? { objectPosition: heroImagePosition } : undefined}
          />
          <div className="absolute inset-0 tf-hero-overlay" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-white">
          <span className="eyebrow !text-[#F2A57C]">{eyebrow}</span>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.06] tracking-tight max-w-4xl tf-hero-text-shadow">
            {title}
          </h1>
          <p className="mt-4 text-[#E8EAE5] text-base md:text-lg leading-relaxed max-w-2xl tf-hero-text-shadow">
            {intro}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/kontakt#offert"
              data-testid="service-hero-cta-quote"
              className="inline-flex items-center justify-center gap-2 bg-[#D95D2E] hover:bg-[#B84B22] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors shadow-lg shadow-black/20"
            >
              Få offert <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${COMPANY.phoneE164}`}
              data-testid="service-hero-cta-phone"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/85 hover:bg-white hover:text-[#1B2A1E] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors backdrop-blur-sm"
            >
              <Phone className="w-4 h-4" /> Ring {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Body / bullets */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            {bodyHeading && (
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1B2A1E] mb-5 leading-tight">
                {bodyHeading}
              </h2>
            )}
            {bodyText && (
              <p className="text-[#3a4338] text-lg leading-relaxed">
                {bodyText}
              </p>
            )}
          </div>

          {bullets.length > 0 && (
            <ul className="lg:col-span-5 space-y-3">
              {bullets.map((b, i) => (
                <li
                  key={i}
                  data-testid={`service-bullet-${i + 1}`}
                  className="flex items-start gap-3 bg-white border border-[#E2E0D8] rounded-lg p-4"
                >
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#1B2A1E] text-[#D95D2E] shrink-0 mt-0.5">
                    <Check className="w-4 h-4" strokeWidth={3} />
                  </span>
                  <div>
                    {typeof b === "string" ? (
                      <span className="text-[#18221A] font-medium leading-relaxed">
                        {b}
                      </span>
                    ) : (
                      <>
                        <span className="block font-heading font-bold text-[#1B2A1E]">
                          {b.title}
                        </span>
                        <span className="text-[#3a4338] text-[15px] leading-relaxed">
                          {b.text}
                        </span>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Shared service gallery — same on every service page */}
      <ServiceGallery />

      {extraSection}

      {/* Mini process */}
      <section className="section-pad bg-[#EFEBDF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <span className="eyebrow">Så jobbar vi</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold text-[#1B2A1E] leading-tight">
              Enkelt, tryggt och tydligt
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {stepList.map((s, i) => (
              <div
                key={s.title}
                data-testid={`service-step-${i + 1}`}
                className="bg-white border border-[#E2E0D8] rounded-xl p-6"
              >
                <div className="font-heading text-3xl font-extrabold text-[#D95D2E]/30 mb-2 tabular-nums">
                  0{i + 1}
                </div>
                <h3 className="font-heading font-bold text-lg text-[#1B2A1E] mb-2">
                  {s.title}
                </h3>
                <p className="text-[#3a4338] text-[15px] leading-relaxed">
                  {s.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {faqItems && faqItems.length > 0 && (
        <FAQ
          id={faqId}
          title={faqTitle}
          items={faqItems}
        />
      )}

      <ContactCTA />
    </div>
  );
}
